import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Modal, message } from "antd";
import { useTranslation } from "react-i18next";

import { registerUser } from "../../authPatient/redux/thunks/authThunks";
import { selectAuthLoading, selectAuthErrorMessage } from "../redux/selectors/authSelectors";

import { mapSignupPayload } from "../functions/auth/mapSignupPayload";
import { axiosGateway } from "../../api/axios";

export default function useSignupPatient() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const loading = useSelector(selectAuthLoading);
  const authError = useSelector(selectAuthErrorMessage);

  const [formData, setFormData] = useState({
    gender: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });

  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyConfirmed, setPrivacyConfirmed] = useState(false);
  const [errors, setErrors] = useState({});
  const [localError, setLocalError] = useState("");
  const [referralToken, setReferralToken] = useState(() => {
    const token = new URLSearchParams(location.search).get("ref");
    if (token) sessionStorage.setItem("medilinkReferralToken", token);
    return token || sessionStorage.getItem("medilinkReferralToken") || "";
  });

  useEffect(() => {
    if (!referralToken) return;
    axiosGateway.post(`/referrals/${encodeURIComponent(referralToken)}/click`).catch(() => {});
  }, [referralToken]);

  const handleInputChange = useCallback((field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: undefined }));
    setLocalError("");
  }, []);

  const handleGenderChange = useCallback((value) => {
    setFormData((p) => ({ ...p, gender: value }));
    setErrors((p) => ({ ...p, gender: undefined }));
  }, []);

  const handleShowPrivacyPopup = useCallback(() => setShowPrivacyModal(true), []);

  const handleConfirmPrivacy = useCallback(() => {
    setFormData((p) => ({ ...p, acceptTerms: true }));
    setPrivacyConfirmed(true);
    setShowPrivacyModal(false);
    setErrors((p) => ({ ...p, acceptTerms: undefined }));
  }, []);

  const validateForm = useCallback(() => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = t("signup.firstNameRequired");
    if (!formData.lastName.trim()) e.lastName = t("signup.lastNameRequired");
    if (!formData.dateOfBirth) e.dateOfBirth = t("signup.dateOfBirthRequired");
    if (!formData.email.trim()) e.email = t("signup.emailRequired");
    if (!formData.phoneNumber.trim()) e.phoneNumber = t("signup.phoneRequired");
    if (!formData.gender) e.gender = t("signup.genderRequired");

    // === Password rules ===
    const pwd = formData.password || "";
    const hasMinLength = pwd.length >= 8;
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasLowercase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[@$!%*?&]/.test(pwd);

    if (!pwd) {
      e.password = t("signup.passwordRequired");
    } else if (!(hasMinLength && hasUppercase && hasLowercase && hasNumber && hasSpecial)) {
      e.password = t("signup.passwordInvalid", "Password does not meet all requirements");
    }

    if (!formData.confirmPassword) {
      e.confirmPassword = t("signup.confirmPasswordRequired");
    } else if (formData.password !== formData.confirmPassword) {
      e.confirmPassword = t("signup.passwordsDontMatch");
    }

    if (!formData.acceptTerms) e.acceptTerms = t("signup.acceptTermsRequired");

    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData, t]);

  const handleCreateAccount = useCallback(async () => {
    setLocalError("");

    if (!validateForm()) return;

    const payload = mapSignupPayload({ ...formData, referralToken });

    try {
      await dispatch(registerUser(payload)).unwrap();

      if (referralToken) sessionStorage.removeItem("medilinkReferralToken");

      message.success(t("signup.accountCreatedSuccess", "Account created. Please verify your email."));
      navigate("/loginpatient");
    } catch (err) {
      const fallbackMsg = t("signup.registrationFailed");
      const msg =
        typeof err === "string"
          ? err
          : typeof err?.error === "string"
          ? err.error
          : typeof err?.message === "string"
          ? err.message
          : fallbackMsg;

      setLocalError(msg);

      Modal.error({
        title: t("signup.registrationFailedTitle"),
        content: msg,
      });
    }
  }, [dispatch, formData, navigate, referralToken, t, validateForm]);

  return {
    formData,
    showPrivacyModal,
    privacyConfirmed,
    errors,
    localError,
    loading,
    authError,

    setFormData,
    setPrivacyConfirmed,
    setShowPrivacyModal,

    handleInputChange,
    handleGenderChange,
    handleShowPrivacyPopup,
    handleConfirmPrivacy,
    handleCreateAccount,
  };
}